/**
 * RIS text → reference records, through the site's own RIS reader (`parseRis`), so the
 * reading rules — tag forms, continuation lines, legacy tags — are the ones the RIS File
 * Validator uses.
 *
 * Where a field has several tags, the order below is the order tried: the journal is T2
 * (the specification), then the legacy JF, JO, JA; the title is TI then T1; the year is
 * PY then Y1 then the year inside DA.
 */

import { parseRis, type RisField } from "@/lib/tool-kit/identifiers/ris-check";
import {
  cleanDoi,
  emptyRecord,
  nameFromText,
  type RefName,
  type RefRecord,
  splitPages,
} from "@/lib/tool-kit/research-discovery/records";

const RE_YEAR = /\b(1[5-9]\d\d|20\d\d)\b/;

function all(fields: readonly RisField[], ...tags: string[]): string[] {
  return fields
    .filter((field) => tags.includes(field.tag))
    .map((field) => field.value.trim())
    .filter((value) => value !== "");
}

function firstOf(fields: readonly RisField[], ...tags: string[]): string {
  for (const tag of tags) {
    const [value] = all(fields, tag);
    if (value !== undefined) {
      return value;
    }
  }
  return "";
}

function person(raw: string): RefName {
  const name = nameFromText(raw);
  return raw.includes(",") ? name : { family: raw, given: "", literal: raw };
}

/** One RIS record's fields as a reference record. */
export function risFieldsToRecord(
  type: string | null,
  fields: readonly RisField[]
): RefRecord {
  const record = emptyRecord(type ?? "GEN");
  record.authors = all(fields, "AU", "A1").map(person);
  record.editors = all(fields, "A2", "ED").map(person);
  record.title = firstOf(fields, "TI", "T1");
  record.container = firstOf(fields, "T2", "JF", "JO", "BT");
  record.containerAbbrev = firstOf(fields, "J2", "JA");
  record.series = firstOf(fields, "T3");
  record.year = RE_YEAR.exec(firstOf(fields, "PY", "Y1", "DA"))?.[1] ?? "";
  record.volume = firstOf(fields, "VL");
  record.issue = firstOf(fields, "IS", "CP");
  record.startPage = firstOf(fields, "SP");
  record.endPage = firstOf(fields, "EP");
  if (record.endPage === "" && record.startPage.includes("-")) {
    [record.startPage, record.endPage] = splitPages(record.startPage);
  }
  record.publisher = firstOf(fields, "PB");
  record.place = firstOf(fields, "CY");
  record.abstract = firstOf(fields, "AB", "N2");
  record.doi = cleanDoi(firstOf(fields, "DO"));
  record.url = firstOf(fields, "UR");
  record.language = firstOf(fields, "LA");
  record.keywords = all(fields, "KW");
  record.accession = firstOf(fields, "AN");
  record.database = firstOf(fields, "DB");
  record.notes = all(fields, "N1");
  return record;
}

/** Every record in a RIS file. Records with neither title nor abstract are dropped. */
export function risToRecords(text: string): RefRecord[] {
  return parseRis(text)
    .records.map((record) => risFieldsToRecord(record.type, record.fields))
    .filter((record) => record.title !== "" || record.abstract !== "");
}
